import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";

/* ------------------------------------------------------------------ */
/*  Service catalogue — display only. Booking links stay unchanged.    */
/* ------------------------------------------------------------------ */
const SERVICES = [
  {
    id: "airport",
    n: "01",
    name: "Airport Transfers",
    tagline: "Flight-aware precision.",
    body: "We monitor your flight in real time and adjust for early arrivals or delays. A chauffeur waits inside the terminal — quietly, and without fuss.",
    highlights: [
      "Real-time flight tracking",
      "Meet & Greet inside the terminal",
      "Sixty minutes complimentary wait",
    ],
    // Book Airport Transfer — preselects the service in the wizard
    ctaHref: "/book?service=Airport+Transfers&mode=booking",
    ctaLabel: "Book Airport Transfer",
    image:
      "https://images.pexels.com/photos/15774577/pexels-photo-15774577.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600",
    layout: "flagship",
    theme: "dark",
  },
  {
    id: "corporate",
    n: "02",
    name: "Corporate Travel",
    tagline: "Executive movement, precisely arranged.",
    body: "Meetings, roadshows and client transfers — discreetly delivered on the schedule you set. Consolidated invoicing and account management for executive assistants and travel desks.",
    highlights: [
      "Traveller profiles & preferences",
      "Consolidated monthly invoicing",
      "Sydney-wide route knowledge",
    ],
    ctaHref: "/book?service=Corporate+Travel&mode=booking",
    ctaLabel: "Arrange Corporate Travel",
    image:
      "https://static.prod-images.emergentagent.com/jobs/3aeb2b71-c3a3-4775-b2b3-77824c8c9518/images/714374406b120efa3a60f16aeb75efc2b782f85afe89042d6d0bc64958e1a386.jpeg",
    layout: "split-image-left",
    theme: "stone",
  },
  {
    id: "events",
    n: "03",
    name: "Events & Occasions",
    tagline: "For evenings that matter.",
    body: "Weddings, galas, private dinners and celebrations. A single chauffeur or a co-ordinated multi-vehicle arrival — handled quietly, on cue.",
    highlights: [
      "Multi-vehicle co-ordination",
      "Timed arrivals & staged departures",
      "Discreet presence throughout the evening",
    ],
    ctaHref: "/book?service=Special+Events&mode=booking",
    ctaLabel: "Arrange Event Transport",
    image:
      "https://static.prod-images.emergentagent.com/jobs/3aeb2b71-c3a3-4775-b2b3-77824c8c9518/images/ceb8bf6c3e8122c2f235bfb4c4d5365374b25d6191c2176d7121bdc504fc4b59.jpeg",
    layout: "split-image-right",
    theme: "ivory",
  },
  {
    id: "hourly",
    n: "04",
    name: "Hourly Hire",
    tagline: "A dedicated chauffeur for the day.",
    body: "Appointments, meetings, viewings, dinner reservations — a chauffeur remains with you between destinations, ready to move when you are.",
    highlights: [
      "Minimum three-hour engagement",
      "Route planned around your day",
      "Same chauffeur, from first to last stop",
    ],
    ctaHref: "/book?service=Hourly+Hire&mode=booking",
    ctaLabel: "Book by the Hour",
    image:
      "https://static.prod-images.emergentagent.com/jobs/3aeb2b71-c3a3-4775-b2b3-77824c8c9518/images/8ec3520a33cc28140e711c6988ab40f8a3fe0239b144f12ac99de9ad643c8b3b.jpeg",
    layout: "split-image-left",
    theme: "dark",
  },
  {
    id: "hotel",
    n: "05",
    name: "Hotel Transfers",
    tagline: "Discreet arrivals at Sydney's finest.",
    body: "Coordinated pickups from and to five-star hotels and private residences. Chauffeurs familiar with concierge protocols and preferred entrances.",
    highlights: [
      "Established concierge relationships",
      "Prompt kerbside collection",
      "Luggage handled discreetly",
    ],
    ctaHref: "/book?service=Corporate+Travel&mode=booking",
    ctaLabel: "Book Hotel Transfer",
    image:
      "https://images.pexels.com/photos/57389/milsons-point-sydney-australia-sydney-opera-house-57389.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600",
    layout: "split-image-right",
    theme: "ivory",
  },
  {
    id: "long-distance",
    n: "06",
    name: "Long-Distance Travel",
    tagline: "Beyond Sydney, on your terms.",
    body: "The Hunter Valley, the Southern Highlands, the Blue Mountains — refined regional travel by chauffeured car, with unhurried stops if you wish.",
    highlights: [
      "Regional NSW expertise",
      "Route planned around your itinerary",
      "Multi-stop journeys handled with care",
    ],
    ctaHref: "/book?service=Hourly+Hire&mode=booking",
    ctaLabel: "Plan Regional Journey",
    image:
      "https://images.pexels.com/photos/2036544/pexels-photo-2036544.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600",
    layout: "cinematic-close",
    theme: "dark",
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="bg-near-black">
      <Navbar transparent />

      <ServicesHero />

      {SERVICES.map((s) => (
        <ServiceSection key={s.id} service={s} />
      ))}

      <HowItWorksEditorial />

      <ServicesClosing />

      <Footer />
      <FloatingActions />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Hero                                                                */
/* ------------------------------------------------------------------ */
function ServicesHero() {
  return (
    <section
      data-testid="services-hero-section"
      className="relative min-h-[80vh] md:min-h-[90vh] flex items-end overflow-hidden bg-near-black"
    >
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1485291571150-772bcfc10da5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBibGFjayUyMGNhciUyMG5pZ2h0JTIwY2hhdWZmZXVyfGVufDB8fHx8MTc4NzM3ODYwNnww&ixlib=rb-4.1.0&q=85"
          alt="Chauffeured black sedan"
          className="w-full h-full object-cover sjc-slow-reveal"
          loading="eager"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(3,5,7,0.6) 0%, rgba(3,5,7,0.35) 45%, rgba(3,5,7,0.9) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(3,5,7,0.85) 0%, rgba(3,5,7,0.35) 55%, rgba(3,5,7,0) 100%)",
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-[92rem] mx-auto px-6 lg:px-12 pb-16 md:pb-24 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="sjc-eyebrow text-warm-white/70 flex items-center gap-4"
        >
          <span className="sjc-hairline w-10" />
          Services
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          data-testid="services-hero-heading"
          className="font-display mt-8 text-warm-white text-5xl sm:text-6xl md:text-7xl lg:text-[96px] leading-[1.02] tracking-[-0.02em] font-light max-w-4xl"
        >
          Six ways to arrive,
          <br />
          <span className="italic">one standard throughout.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 text-warm-white/70 text-base md:text-lg font-light leading-relaxed max-w-xl"
        >
          Whether the journey begins at a terminal, a boardroom or a five-star lobby,
          the service is the same: quietly precise, personally attended.
        </motion.p>
      </div>

      <div className="absolute bottom-6 right-8 md:right-12 z-10 flex items-center gap-3 text-warm-white/50">
        <span className="sjc-eyebrow">Scroll</span>
        <span className="w-16 h-px bg-gold/50" />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  A single service — chooses composition variant from `layout`        */
/* ------------------------------------------------------------------ */
function ServiceSection({ service }) {
  if (service.layout === "flagship") return <ServiceFlagship service={service} />;
  if (service.layout === "cinematic-close") return <ServiceCinematicClose service={service} />;
  return <ServiceSplit service={service} />;
}

/* Full-bleed flagship — the most weight (Airport Transfers) */
function ServiceFlagship({ service }) {
  return (
    <section
      id={`service-${service.id}`}
      data-testid={`service-section-${service.id}`}
      className="relative bg-near-black border-t border-white/10"
    >
      <div className="relative h-[80vh] md:h-[92vh] overflow-hidden">
        <img
          src={service.image}
          alt={service.name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={onImgError}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(3,5,7,0.3) 0%, rgba(3,5,7,0.15) 45%, rgba(3,5,7,0.9) 100%)",
          }}
        />

        <div className="absolute inset-0 flex flex-col justify-between max-w-[92rem] mx-auto px-6 lg:px-12 py-16 md:py-24">
          <motion.p
            {...fadeUp}
            className="sjc-eyebrow text-warm-white/75 flex items-center gap-4"
          >
            <span className="sjc-hairline w-10" />
            {service.n} · Flagship Service
          </motion.p>

          <div className="max-w-3xl">
            <motion.h2
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="font-display text-warm-white text-5xl md:text-6xl lg:text-[88px] leading-[1] tracking-[-0.02em]"
            >
              {splitItalicLast(service.name)}
            </motion.h2>
            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.15 }}
              className="mt-6 font-display italic text-2xl md:text-3xl text-gold/85"
            >
              {service.tagline}
            </motion.p>
          </div>
        </div>
      </div>

      <div className="bg-ivory border-t border-charcoal/10">
        <div className="max-w-[92rem] mx-auto px-6 lg:px-12 py-20 md:py-28 grid lg:grid-cols-12 gap-14 lg:gap-24">
          <div className="lg:col-span-6">
            <p className="text-charcoal/75 text-lg md:text-xl font-light leading-[1.7] max-w-lg">
              {service.body}
            </p>
          </div>
          <div className="lg:col-span-6">
            <ul className="space-y-6 border-t border-charcoal/10">
              {service.highlights.map((h) => (
                <li
                  key={h}
                  className="flex items-baseline gap-5 pt-6 border-b border-charcoal/10 pb-6 last:border-b-0"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-gold mt-1.5 flex-shrink-0" />
                  <span className="font-display text-charcoal text-xl md:text-2xl leading-snug">
                    {h}
                  </span>
                </li>
              ))}
            </ul>
            <ServiceCTA service={service} className="mt-12" isIvory />
          </div>
        </div>
      </div>
    </section>
  );
}

/* Alternating editorial split */
function ServiceSplit({ service }) {
  const imageLeft = service.layout === "split-image-left";
  const isLight = service.theme !== "dark";

  const bgClass =
    service.theme === "stone" ? "bg-stone" : isLight ? "bg-ivory" : "bg-near-black";
  const borderTop = isLight ? "border-charcoal/10" : "border-white/10";
  const textPrimary = isLight ? "text-charcoal" : "text-warm-white";
  const textMuted = isLight ? "text-muted-light" : "text-warm-white/65";
  const textEyebrow = isLight ? "text-muted-light" : "text-warm-white/50";
  const borderRow = isLight ? "border-charcoal/10" : "border-white/10";

  return (
    <section
      id={`service-${service.id}`}
      data-testid={`service-section-${service.id}`}
      className={`relative py-24 md:py-32 border-t ${borderTop} ${bgClass}`}
    >
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className={`lg:col-span-7 ${imageLeft ? "lg:order-1" : "lg:order-2"}`}
        >
          <div className={`relative overflow-hidden ${isLight ? "bg-stone" : "bg-surface-dark"}`}>
            <img
              src={service.image}
              alt={service.name}
              className="w-full h-[420px] md:h-[560px] lg:h-[620px] object-cover"
              loading="lazy"
              onError={onImgError}
            />
            {!isLight && (
              <div className="absolute inset-0 bg-gradient-to-t from-near-black/40 via-transparent to-transparent" />
            )}
          </div>
        </motion.div>

        {/* Copy */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className={`lg:col-span-5 ${imageLeft ? "lg:order-2" : "lg:order-1"}`}
        >
          <p className={`sjc-eyebrow ${textEyebrow} flex items-center gap-3`}>
            <span className="font-display text-gold/80 italic text-base">{service.n}</span>
            <span className="sjc-hairline w-6" />
            <span>Service</span>
          </p>

          <h2
            className={`font-display mt-6 text-4xl md:text-5xl lg:text-[60px] leading-[1.05] tracking-[-0.015em] ${textPrimary}`}
          >
            {splitItalicLast(service.name)}
          </h2>

          <p
            className={`mt-4 font-display italic text-lg md:text-xl ${
              isLight ? "text-muted-light" : "text-gold/85"
            }`}
          >
            {service.tagline}
          </p>

          <p className={`mt-6 text-[15px] md:text-base font-light leading-[1.8] max-w-lg ${textMuted}`}>
            {service.body}
          </p>

          <ul className={`mt-8 pt-6 border-t ${borderRow} space-y-3`}>
            {service.highlights.map((h) => (
              <li key={h} className="flex items-start gap-3.5">
                <span className="w-1 h-1 rounded-full bg-gold mt-2 flex-shrink-0" />
                <span className={`text-sm md:text-[15px] ${textPrimary}/80 font-light`}>
                  {h}
                </span>
              </li>
            ))}
          </ul>

          <ServiceCTA service={service} className="mt-10" isIvory={isLight} />
        </motion.div>
      </div>
    </section>
  );
}

/* Cinematic full-bleed close for the last service (Long-Distance) */
function ServiceCinematicClose({ service }) {
  return (
    <section
      id={`service-${service.id}`}
      data-testid={`service-section-${service.id}`}
      className="relative bg-near-black border-t border-white/10"
    >
      <div className="relative h-[70vh] md:h-[80vh] overflow-hidden">
        <img
          src={service.image}
          alt={service.name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={onImgError}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(3,5,7,0.9) 0%, rgba(3,5,7,0.55) 50%, rgba(3,5,7,0.1) 100%)",
          }}
        />

        <div className="relative z-10 h-full max-w-[92rem] mx-auto px-6 lg:px-12 flex items-center">
          <div className="max-w-xl">
            <motion.p
              {...fadeUp}
              className="sjc-eyebrow text-warm-white/70 flex items-center gap-3"
            >
              <span className="font-display text-gold/80 italic text-base">{service.n}</span>
              <span className="sjc-hairline w-6" />
              <span>Service</span>
            </motion.p>

            <motion.h2
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="font-display mt-6 text-warm-white text-4xl md:text-5xl lg:text-[68px] leading-[1.02] tracking-[-0.015em]"
            >
              {splitItalicLast(service.name)}
            </motion.h2>

            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.15 }}
              className="mt-4 font-display italic text-lg md:text-xl text-gold/85"
            >
              {service.tagline}
            </motion.p>

            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.2 }}
              className="mt-6 text-warm-white/70 text-[15px] md:text-base font-light leading-[1.75] max-w-md"
            >
              {service.body}
            </motion.p>

            <ServiceCTA service={service} className="mt-10" />
          </div>
        </div>
      </div>
    </section>
  );
}

function ServiceCTA({ service, className = "", primary = false, isIvory = false }) {
  const solidDark =
    "bg-warm-white text-midnight hover:bg-gold";
  const outlineDark =
    "border border-gold/40 text-warm-white hover:border-gold hover:bg-gold/5";
  const solidLight =
    "bg-midnight text-warm-white hover:bg-gold hover:text-midnight";

  const classes = isIvory ? solidLight : primary ? solidDark : outlineDark;

  return (
    <Link
      to={service.ctaHref}
      data-testid={`service-cta-${service.id}`}
      className={`group inline-flex items-center gap-3 px-7 py-3.5 text-[11px] tracking-[0.28em] uppercase font-semibold transition-all duration-500 ${classes} ${className}`}
    >
      <span>{service.ctaLabel}</span>
      <span
        className={`w-1.5 h-1.5 rounded-full transition-transform duration-500 group-hover:translate-x-1 ${
          isIvory ? "bg-warm-white group-hover:bg-midnight" : primary ? "bg-midnight" : "bg-gold"
        }`}
      />
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/*  How It Works — refined 3-step editorial                             */
/* ------------------------------------------------------------------ */
function HowItWorksEditorial() {
  const steps = [
    {
      n: "01",
      title: "Tell us your journey.",
      body: "Enter where you're travelling, when, and who's coming. The wizard reveals only the details we genuinely need.",
    },
    {
      n: "02",
      title: "We recommend the right vehicle.",
      body: "Passenger and luggage information guides an appropriate class, while relevant choices remain yours to make.",
    },
    {
      n: "03",
      title: "Review and confirm.",
      body: "Review the itinerary, verify the all-inclusive fare, then complete the reservation securely.",
    },
  ];

  return (
    <section
      data-testid="services-how-it-works-section"
      className="bg-ivory py-24 md:py-36 border-t border-charcoal/10"
    >
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-24">
        <div className="lg:col-span-4">
          <p className="sjc-eyebrow text-muted-light flex items-center gap-4">
            <span className="sjc-hairline w-8" />
            Booking
          </p>
          <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.015em] text-charcoal">
            The reservation,
            <br />
            <span className="italic font-light">simplified.</span>
          </h2>
          <p className="mt-6 text-muted-light text-base md:text-lg font-light leading-[1.75] max-w-md">
            Three quiet steps. The website handles everything else — flight tracking,
            vehicle selection, fare calculation — so the decision remains yours alone.
          </p>
        </div>

        <ol className="lg:col-span-8 divide-y divide-charcoal/10 border-t border-charcoal/10">
          {steps.map((s, i) => (
            <motion.li
              key={s.n}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`how-step-${s.n}`}
              className="py-10 md:py-12 grid grid-cols-[auto_1fr] gap-6 md:gap-14"
            >
              <span className="font-display text-gold/80 italic text-3xl md:text-4xl pt-1">
                {s.n}
              </span>
              <div>
                <h3 className="font-display text-3xl md:text-[36px] leading-[1.15] tracking-tight text-charcoal">
                  {s.title}
                </h3>
                <p className="mt-4 text-muted-light text-[15px] md:text-base font-light leading-[1.8] max-w-xl">
                  {s.body}
                </p>
              </div>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Closing invitation                                                  */
/* ------------------------------------------------------------------ */
function ServicesClosing() {
  return (
    <section
      data-testid="services-closing-section"
      className="relative overflow-hidden bg-near-black text-warm-white border-t border-white/10"
    >
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1605437241278-c1806d14a4d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXIlMjBpbnRlcmlvciUyMGxlYXRoZXIlMjBkZXRhaWx8ZW58MHx8fHwxNzg3Mzc4NjA2fDA&ixlib=rb-4.1.0&q=85"
          alt="Vehicle interior detail"
          className="w-full h-full object-cover opacity-30"
          loading="lazy"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(3,5,7,0.75) 0%, rgba(3,5,7,0.85) 100%)",
          }}
        />
      </div>

      <div className="relative z-10 max-w-[92rem] mx-auto px-6 lg:px-12 py-32 md:py-48">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl"
        >
          <p className="sjc-eyebrow text-gold flex items-center gap-4">
            <span className="sjc-hairline w-10" />
            Ready when you are
          </p>
          <h2 className="font-display mt-8 text-5xl md:text-6xl lg:text-[80px] leading-[1.02] tracking-[-0.02em]">
            Wherever the journey takes you,
            <br />
            <span className="italic font-light">we&apos;ll handle the details.</span>
          </h2>
          <p className="mt-8 text-warm-white/70 text-base md:text-lg font-light leading-relaxed max-w-xl">
            Reserve online in a few quiet steps, or speak directly with our concierge team.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-8">
            <Link
              to="/book"
              data-testid="services-closing-cta"
              className="group inline-flex items-center gap-3 bg-warm-white text-midnight px-8 py-4 text-[11px] tracking-[0.28em] uppercase font-semibold transition-all duration-500 hover:bg-gold"
            >
              <span>Book Your Journey</span>
              <span className="w-2 h-2 rounded-full bg-midnight transition-transform duration-500 group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:+61400000000"
              data-testid="services-closing-phone"
              className="group flex items-center gap-4 text-warm-white/85 hover:text-warm-white transition-colors duration-300"
            >
              <span className="w-8 h-px bg-gold transition-all duration-500 group-hover:w-12" />
              <div>
                <p className="sjc-eyebrow text-warm-white/50">Speak with us</p>
                <p className="mt-1 font-display text-xl md:text-2xl tracking-tight">
                  +61 400 000 000
                </p>
              </div>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */
const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

function splitItalicLast(text) {
  const words = text.split(" ");
  return words.map((word, i, arr) =>
    i === arr.length - 1 ? (
      <span key={i} className="italic font-light">
        {word}
      </span>
    ) : (
      <span key={i}>{word} </span>
    ),
  );
}

function onImgError(e) {
  e.currentTarget.style.display = "none";
  e.currentTarget.parentElement.classList.add(
    "bg-gradient-to-br",
    "from-surface-dark",
    "to-near-black",
  );
}
